<template>
    <div class="random-chat-view">
        <div
            class="position-relative bg-dark rounded overflow-hidden remote-panel mb-3"
        >
            <video
                ref="remoteVideo"
                class="w-100 h-100"
                autoplay
                playsinline
                controls
                v-show="hasRemoteVideoTrack"
            ></video>
            <div
                v-if="!hasRemoteVideoTrack"
                class="d-flex flex-column align-items-center justify-content-center text-white py-5"
            >
                <font-awesome-icon
                    size="3x"
                    icon="fa-duotone fa-sparkles"
                    class="mb-2"
                />
                <div class="fw-semibold">Waiting for {{ remoteLabel }}</div>
                <small class="text-white-50">Connected to private room</small>
            </div>

            <video
                ref="localVideo"
                class="local-preview position-absolute border border-light rounded"
                autoplay
                playsinline
                muted
            ></video>
        </div>

        <div class="d-flex align-items-center justify-content-center flex-wrap gap-2">
            <b-button
                class="call-button"
                :class="{ 'text-danger': !isCameraOn }"
                variant="secondary"
                @click="toggleCamera"
                :disabled="connecting"
            >
                <font-awesome-icon
                    v-if="isCameraOn"
                    icon="fa-duotone fa-camera"
                />
                <font-awesome-icon v-else icon="fa-duotone fa-camera-slash" />
            </b-button>

            <b-button
                class="call-button"
                :class="{ 'text-danger': !isMicrophoneOn }"
                variant="secondary"
                @click="toggleMicrophone"
                :disabled="connecting"
            >
                <font-awesome-icon
                    v-if="isMicrophoneOn"
                    icon="fa-duotone fa-microphone"
                />
                <font-awesome-icon
                    v-else
                    icon="fa-duotone fa-microphone-slash"
                />
            </b-button>

            <b-button
                class="call-button"
                variant="danger"
                @click="hangup"
                :disabled="connecting"
            >
                <font-awesome-icon icon="fa-duotone fa-phone-hangup" />
            </b-button>
        </div>

        <div class="text-center mt-3">
            <small class="text-muted" v-if="!callError">{{ statusText }}</small>
            <small class="text-danger" v-else>{{ callError }}</small>
        </div>
    </div>
</template>

<script>
import {
    getDevices,
    createCameraVideoTrack,
    createMicrophoneAudioTrack,
} from "agora-rtc-sdk-ng/esm";
import { createClient } from "agora-rtc-sdk-ng/esm";
import { mapState } from "vuex";

import {
    CALL_AUDIO_DEVICE_ID,
    CALL_VIDEO_DEVICE_ID,
    CALL_AUDIO_ENABLED,
    CALL_VIDEO_ENABLED,
} from "@/constant/localStorage";

export default {
    name: "RandomChatView",
    props: {
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            client: null,
            joinedChannel: null,
            videoTrack: null,
            audioTrack: null,
            remoteVideoTrack: null,
            audioDevices: [],
            videoDevices: [],
            audioDeviceId: localStorage.getItem(CALL_AUDIO_DEVICE_ID),
            videoDeviceId: localStorage.getItem(CALL_VIDEO_DEVICE_ID),
            isCameraOn: false,
            isMicrophoneOn: false,
            hasRemoteVideoTrack: false,
            isAudioSubscribed: false,
            connecting: false,
            callError: "",
            eventHandlers: {
                published: null,
                unpublished: null,
            },
        };
    },
    computed: {
        ...mapState({
            onboardingUser: (state) => state.onboarding.user,
        }),
        ...mapState("options", ["agora"]),
        channelName() {
            if (!this.onboardingUser?.id || !this.user?.id) return null;
            const sorted = [this.onboardingUser.id, this.user.id]
                .map((value) => String(value))
                .sort();
            return `randomChat-${sorted.join("-")}`;
        },
        remoteLabel() {
            return this.user?.id ? `#${this.user.id}` : "guest";
        },
        statusText() {
            if (this.callError) return this.callError;
            if (this.connecting) return "Connecting to Agora...";
            if (this.hasRemoteVideoTrack) return `Live with ${this.remoteLabel}`;
            if (this.joinedChannel) return "Waiting for remote video...";
            return "Preparing private call...";
        },
    },
    watch: {
        channelName: {
            handler() {
                this.maybeStartSession();
            },
            immediate: true,
        },
        agora: {
            handler() {
                this.maybeStartSession();
            },
            deep: false,
        },
    },
    mounted() {
        this.maybeStartSession();
    },
    async beforeDestroy() {
        await this.teardownSession();
    },
    async beforeUnmount() {
        await this.teardownSession();
    },
    methods: {
        async maybeStartSession() {
            if (
                this.connecting ||
                !this.channelName ||
                !this.agora?.appId ||
                !this.onboardingUser?.id
            ) {
                return;
            }

            if (this.joinedChannel === this.channelName && this.client) {
                return;
            }

            await this.startSession();
        },
        async startSession() {
            this.connecting = true;
            this.callError = "";

            try {
                await this.$nextTick();

                if (!this.client) {
                    this.client = await createClient({ mode: "rtc", codec: "vp8" });
                    if (!this.eventHandlers.published) {
                        this.eventHandlers.published = (user, mediaType) =>
                            this.handleUserPublished(user, mediaType);
                    }
                    if (!this.eventHandlers.unpublished) {
                        this.eventHandlers.unpublished = (user, mediaType) =>
                            this.handleUserUnpublished(user, mediaType);
                    }
                    this.client.on("user-published", this.eventHandlers.published);
                    this.client.on("user-unpublished", this.eventHandlers.unpublished);
                }

                if (this.joinedChannel && this.joinedChannel !== this.channelName) {
                    await this.client.leave();
                    this.joinedChannel = null;
                }

                if (!this.joinedChannel) {
                    await this.client.join(
                        this.agora.appId,
                        this.channelName,
                        null,
                        this.onboardingUser.id
                    );
                    this.joinedChannel = this.channelName;
                }

                await this.refreshDevices();
                await this.setMicrophoneState(true);
                await this.setCameraState(true);
            } catch (err) {
                console.error(err);
                this.callError = err?.message || "Failed to start video call.";
            } finally {
                this.connecting = false;
            }
        },
        async refreshDevices() {
            try {
                const devices = await getDevices();
                this.audioDevices = devices.filter((device) => device.kind === "audioinput");
                this.videoDevices = devices.filter((device) => device.kind === "videoinput");
            } catch (err) {
                this.callError = err?.message || "Unable to list devices.";
            }
        },
        async setCameraState(shouldEnable) {
            if (!this.client) return;

            if (shouldEnable) {
                if (!this.videoTrack) {
                    this.videoTrack = await createCameraVideoTrack({
                        facingMode: "user",
                        cameraId: this.videoDeviceId || undefined,
                    });
                    await this.client.publish(this.videoTrack);
                    if (!this.$refs.localVideo) {
                        await this.$nextTick();
                    }
                    this.videoTrack.play(this.$refs.localVideo);
                }

                await this.videoTrack.setMuted(false);
                this.isCameraOn = true;
                localStorage.setItem(CALL_VIDEO_ENABLED, true);
            } else if (this.videoTrack) {
                await this.videoTrack.setMuted(true);
                this.isCameraOn = false;
                localStorage.removeItem(CALL_VIDEO_ENABLED);
            }
        },
        async toggleCamera() {
            await this.setCameraState(!this.isCameraOn);
        },
        async setMicrophoneState(shouldEnable) {
            if (!this.client) return;

            if (shouldEnable) {
                if (!this.audioTrack) {
                    this.audioTrack = await createMicrophoneAudioTrack({
                        microphoneId: this.audioDeviceId || undefined,
                    });
                    await this.client.publish(this.audioTrack);
                }

                await this.audioTrack.setMuted(false);
                this.isMicrophoneOn = true;
                localStorage.setItem(CALL_AUDIO_ENABLED, true);
            } else if (this.audioTrack) {
                await this.audioTrack.setMuted(true);
                this.isMicrophoneOn = false;
                localStorage.removeItem(CALL_AUDIO_ENABLED);
            }
        },
        async toggleMicrophone() {
            await this.setMicrophoneState(!this.isMicrophoneOn);
        },
        async handleUserPublished(user, mediaType) {
            if (!this.client) return;
            await this.client.subscribe(user, mediaType);

            if (mediaType === "video") {
                this.remoteVideoTrack = user.videoTrack;
                this.remoteVideoTrack?.play(this.$refs.remoteVideo);
                this.hasRemoteVideoTrack = true;
            }

            if (mediaType === "audio") {
                user.audioTrack?.play();
                this.isAudioSubscribed = true;
            }
        },
        async handleUserUnpublished(user, mediaType) {
            if (!this.client) return;
            await this.client.unsubscribe(user, mediaType);

            if (mediaType === "video") {
                this.remoteVideoTrack?.stop();
                this.remoteVideoTrack = null;
                this.hasRemoteVideoTrack = false;
            }

            if (mediaType === "audio") {
                this.isAudioSubscribed = false;
            }
        },
        async hangup() {
            await this.teardownSession();
            this.$emit("hangup");
        },
        async teardownSession() {
            try {
                if (this.videoTrack) {
                    await this.videoTrack.stop();
                    await this.videoTrack.close();
                    this.videoTrack = null;
                }

                if (this.audioTrack) {
                    await this.audioTrack.stop();
                    await this.audioTrack.close();
                    this.audioTrack = null;
                }

                this.isCameraOn = false;
                this.isMicrophoneOn = false;

                if (this.client && this.joinedChannel) {
                    await this.client.leave();
                    this.joinedChannel = null;
                }

                if (this.client) {
                    if (this.eventHandlers.published) {
                        this.client.off("user-published", this.eventHandlers.published);
                    }
                    if (this.eventHandlers.unpublished) {
                        this.client.off("user-unpublished", this.eventHandlers.unpublished);
                    }
                    this.client = null;
                }
            } catch (err) {
                console.error(err);
            } finally {
                this.remoteVideoTrack?.stop();
                this.remoteVideoTrack = null;
                this.hasRemoteVideoTrack = false;
                this.isAudioSubscribed = false;
            }
        },
    },
};
</script>

<style scoped>
.remote-panel {
    min-height: 260px;
}

.remote-panel video {
    background: #000;
}

.local-preview {
    width: 160px;
    height: 120px;
    bottom: 1rem;
    right: 1rem;
    object-fit: cover;
    background: #000;
}

.call-button {
    width: 3rem;
    height: 3rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
}
</style>
